// OrgPicker - active-org switcher backed by a shared React context so
// every consumer (Dashboard, Submit, Submissions, nav) reacts to the
// same org state. localStorage persists the selection across sessions.

import { useQueryClient } from '@tanstack/react-query';
import { Building2, Check, ChevronDown } from 'lucide-react';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/classnames';

const STORAGE_KEY = 'voltz.activeOrg';
const LEGACY_STORAGE_KEY = 'threatforge.activeOrg';

function readActiveOrg(): string | null {
  const current = localStorage.getItem(STORAGE_KEY);
  if (current) return current;
  const legacy = localStorage.getItem(LEGACY_STORAGE_KEY);
  if (legacy) {
    localStorage.setItem(STORAGE_KEY, legacy);
    localStorage.removeItem(LEGACY_STORAGE_KEY);
    return legacy;
  }
  return null;
}

// ── Shared context ────────────────────────────────────────────────────

// Sentinel value for the admin "all organizations" view. Not an org id -
// it cannot collide with one, whose shape is "org-<26 chars>". The backend
// List handler recognises this literal string.
export const ALL_ORGS = 'all' as const;

export interface ActiveOrgCtx {
  orgId: string | null;
  pick: (id: string) => void;
}

// Exported so an app that hosts the dashboard's pages can supply its own org
// through <ActiveOrgContext.Provider> instead of ActiveOrgProvider's.
export const ActiveOrgContext = createContext<ActiveOrgCtx>({ orgId: null, pick: () => {} });

export function ActiveOrgProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [orgId, setOrgId] = useState<string | null>(() => readActiveOrg());

  useEffect(() => {
    if (!user) return;
    const memberships = user.memberships ?? [];
    if (memberships.length === 0 && !user.is_admin) {
      setOrgId(null);
      return;
    }
    // Admins may have orgId === ALL_ORGS - that's valid, don't override.
    if (orgId === ALL_ORGS && user.is_admin) return;
    if (!orgId || !memberships.some((m) => m.org_id === orgId)) {
      if (memberships.length > 0) {
        const first = memberships[0].org_id;
        setOrgId(first);
        localStorage.setItem(STORAGE_KEY, first);
      } else {
        setOrgId(null);
      }
    }
  }, [user, orgId]);

  const pick = useCallback((id: string) => {
    setOrgId(id);
    localStorage.setItem(STORAGE_KEY, id);
    void qc.invalidateQueries();
  }, [qc]);

  return (
    <ActiveOrgContext.Provider value={{ orgId, pick }}>
      {children}
    </ActiveOrgContext.Provider>
  );
}

export function useActiveOrg() {
  return useContext(ActiveOrgContext);
}

// ── Dropdown component ────────────────────────────────────────────────

export default function OrgPicker() {
  const { user } = useAuth();
  const { orgId, pick } = useActiveOrg();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const memberships = user?.memberships ?? [];
  const isAdmin = !!user?.is_admin;
  const isAllOrgs = orgId === ALL_ORGS;
  const active = isAllOrgs ? null : memberships.find((m) => m.org_id === orgId);

  useEffect(() => {
    if (!open) return;
    function onPointer(e: PointerEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('pointerdown', onPointer);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointerdown', onPointer);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Admins always get the dropdown (they have the "All" option even with
  // 0-1 memberships). Non-admins: original behavior.
  if (!isAdmin && memberships.length === 0) {
    return (
      <div className="text-xs text-ink-500 italic">No organizations yet - contact your admin.</div>
    );
  }
  if (!isAdmin && memberships.length === 1) {
    return (
      <div
        className="inline-flex items-center gap-2 text-sm text-ink-300 max-w-[16rem] min-w-0"
        title={memberships[0].org_name}
      >
        <Building2 className="w-4 h-4 text-ink-500 shrink-0" />
        <span className="truncate">{memberships[0].org_name}</span>
      </div>
    );
  }

  const buttonLabel = isAllOrgs ? 'All Organizations' : (active?.org_name ?? 'Select organization');

  return (
    <div className="relative" ref={wrapRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md hover:bg-ink-900 border border-ink-800 transition-colors text-sm max-w-[16rem]"
        aria-haspopup="listbox"
        aria-expanded={open}
        title={buttonLabel}
      >
        <Building2 className="w-4 h-4 text-ink-500 shrink-0" />
        <span className="truncate">{buttonLabel}</span>
        <ChevronDown className={cn('w-4 h-4 text-ink-500 shrink-0 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div role="listbox" className="absolute right-0 mt-1.5 w-[min(16rem,calc(100vw-1.5rem))] card p-1 z-50 animate-slide-down">
          {memberships.map((m) => (
            <OrgOption key={m.org_id} m={m} active={m.org_id === orgId} onPick={pick} onClose={() => setOpen(false)} />
          ))}
          {isAdmin && (
            <button
              role="option"
              aria-selected={isAllOrgs}
              onClick={() => { pick(ALL_ORGS); setOpen(false); }}
              className={cn(
                'w-full flex items-center justify-between gap-2 px-3 py-2 text-sm rounded-md hover:bg-ink-800/60 text-left transition-colors border-t border-ink-800 mt-1 pt-2',
                isAllOrgs && 'bg-ink-800/40'
              )}
            >
              <span className="text-ink-100 font-medium truncate">All Organizations</span>
              {isAllOrgs && <Check className="w-4 h-4 text-accent-400 shrink-0" />}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function OrgOption({
  m,
  active,
  onPick,
  onClose,
}: {
  m: { org_id: string; org_name: string; role: string };
  active: boolean;
  onPick: (id: string) => void;
  onClose: () => void;
}) {
  const handleClick = useCallback(() => {
    onPick(m.org_id);
    onClose();
  }, [m.org_id, onPick, onClose]);

  return (
    <button
      role="option"
      aria-selected={active}
      onClick={handleClick}
      title={m.org_name}
      className={cn(
        'w-full flex items-center justify-between gap-2 px-3 py-2 text-sm rounded-md hover:bg-ink-800/60 text-left transition-colors',
        active && 'bg-ink-800/40'
      )}
    >
      <div className="min-w-0 flex-1">
        <div className="text-ink-100 font-medium truncate">{m.org_name}</div>
        <div className="text-xs text-ink-500 truncate">
          {(m.role === 'org_admin' || m.role === 'manager') ? 'Management' : 'Member'}
        </div>
      </div>
      {active && <Check className="w-4 h-4 text-accent-400 shrink-0" />}
    </button>
  );
}
